<template>
  <NuxtLayout>
    <NuxtPage />
  </NuxtLayout>
  <ClientOnly><CookieConsent v-if="!isEmbedPath(route.path)" /></ClientOnly>
</template>

<script setup>
import releaseNotePageNames from '#build/page-data/release-note-names.mjs';
import releaseLastmods from '#build/page-data/release-lastmod.mjs';
import {
  defaultLocaleCode,
  isLocalizedIndexablePath,
  locales,
  localizedUrl,
  normalizePath,
  stripLocaleFromPath,
} from "~/data/locales";
import { pricingPlans } from "~/data/pricing-plans";
import { getPricingMetaDescription } from "~/data/pricing-localized-copy";
import { noindexRoutePaths, seoRoutes } from "~/data/seo-routes";
import articleDates from "~/data/article-dates.json";
import { articleTypeForPath } from "~/data/article-schema";
import ogLocales from "~/data/og-locales.json";
import { ogImagePathForRoute } from "~/utils/og-image";
import { buildPrimarySoftwareOffer } from "~/utils/structured-data";
import { isEmbedPath } from "~/data/tool-embeds.mjs";

const route = useRoute();
const siteUrl = "https://workspace369.com";
const {
  canonicalLocaleCode,
  currentLocale,
  currentLocaleCode,
  isCurrentPageIndexable,
  pricingCopy,
  seoCopy,
} = useWorkspaceLocale();

// Analytics and marketing tags are no longer injected statically. They load
// from plugins/cookie-consent.client.ts only after the visitor grants the
// matching cookie category (GDPR/CCPA gating).

const currentPath = computed(() => normalizePath(stripLocaleFromPath(route.path)));
const hasLocalizedAlternates = computed(() =>
  isLocalizedIndexablePath(currentPath.value, "ar"),
);

const canonicalUrl = computed(() =>
  localizedUrl(siteUrl, currentPath.value, canonicalLocaleCode.value),
);

// currentPath is already locale-stripped, so it matches the unprefixed
// seoRoutes paths; routes outside the sitemap (auth/internal) get no date.
const seoLastmod = computed(
  () => seoRoutes.find((entry) => entry.path === currentPath.value)?.lastmod,
);
// A newer release card on the page (FeatureReleaseUpdates) moves the emitted lastmod, as it
// moves the sitemap lastmod (utils/release-lastmod.mjs).
const routeLastmod = computed(() => releaseLastmods[currentPath.value] || seoLastmod.value);

// Article-family node for content pages (data/article-schema.ts). Dates come from
// data/article-dates.json (git history, legal front matter) and seo-routes lastmod, never the
// build clock. The headline is filled from the page's own <title> by plugins/single-page-schema.ts,
// which also drops this node where the page emits its own Article or BlogPosting.
const articleNode = computed(() => {
  const dates = articleDates[currentPath.value];
  const type = articleTypeForPath(currentPath.value);
  const contentModified = dates?.modified || seoLastmod.value;
  const dateModified = [contentModified, routeLastmod.value].filter(Boolean).sort().at(-1);
  if (!dates || !type || !dateModified) return null;
  return {
    "@type": type,
    "@id": `${canonicalUrl.value}#article`,
    headline: pageName.value,
    url: canonicalUrl.value,
    mainEntityOfPage: { "@id": `${canonicalUrl.value}#webpage` },
    inLanguage: currentLocale.value.hreflang,
    // A route can be listed later than its content last changed; it was live by then.
    // Release cards never move it: only the page's own dates count.
    datePublished: [dates.published, contentModified].filter(Boolean).sort()[0],
    dateModified,
    author: { "@id": `${siteUrl}/#organization` },
    publisher: { "@id": `${siteUrl}/#organization` },
    about: { "@id": `${siteUrl}/#software` },
    image: socialImageUrl.value,
  };
});

const alternateLinks = computed(() =>
  hasLocalizedAlternates.value
    ? [
        ...locales.map((locale) => ({
          key: `alternate-${locale.hreflang}`,
          rel: "alternate",
          hreflang: locale.hreflang,
          href: localizedUrl(siteUrl, currentPath.value, locale.code),
        })),
        {
          key: "alternate-x-default",
          rel: "alternate",
          hreflang: "x-default",
          href: localizedUrl(siteUrl, currentPath.value, defaultLocaleCode),
        },
      ]
    : [],
);

const pageNames = {
  "/tools/booking-page-generator": "Free Booking Page Generator | Workspace369",
  "/tools/billable-utilization-calculator": "Billable Utilization Calculator | Workspace369",
  "/tools/upwork-vs-fiverr-fee-calculator": "Upwork vs Fiverr Fee Calculator | Workspace369",
  "/tools/seo-deliverables-tracker": "SEO Deliverables Tracker | Workspace369",
  "/tools/scope-change-request-generator": "Scope-change Request Builder | Workspace369",
  "/tools/agency-capacity-planner": "Agency Capacity Planner | Workspace369",
  "/": "Workspace369 | Client Work Operating System",
  "/features": "Features | Workspace369",
  "/solutions": "Solutions | Workspace369",
  "/solutions/service-business-management-software":
    "Service Business Management Software | Workspace369",
  "/solutions/client-work-operating-system": "Client Work Operating System | Workspace369",
  "/solutions/invoicing-and-project-management-software":
    "Invoicing and Project Management Software | Workspace369",
  "/solutions/client-portal-and-billing-software":
    "Client Portal and Billing Software | Workspace369",
  "/solutions/service-business-crm-software": "Service Business CRM Software | Workspace369",
  "/solutions/business-phone-system-with-crm": "Business Phone System with CRM | Workspace369",
  "/solutions/ai-call-summary-software": "AI Call Summary Software | Workspace369",
  "/solutions/sms-inbox-for-client-work": "SMS Inbox for Client Work | Workspace369",
  "/solutions/google-voice-alternative": "Google Voice Alternative | Workspace369",
  "/features/client-scoped-access": "Client-Scoped Access | Workspace369",
  "/features/autopilot": "AI AutoPilot Today | Workspace369",
  "/features/online-booking": "Online Booking | Workspace369",
  "/compare": "Compare Workspace369 | Alternatives and Competitor Comparisons",
  "/compare/replace-your-software-stack":
    "Replace Your Software Stack with Workspace369 | Workspace369",
  "/compare/hubspot": "Workspace369 vs HubSpot | Workspace369",
  "/compare/salesforce": "Workspace369 vs Salesforce | Workspace369",
  "/compare/paymo": "Workspace369 vs Paymo | Workspace369",
  "/compare/honeybook": "HoneyBook vs Workspace369 | Workspace369",
  "/compare/dubsado": "Workspace369 vs Dubsado | Workspace369",
  "/compare/invoice-ninja": "Workspace369 vs Invoice Ninja | Workspace369",
  "/compare/bonsai": "Workspace369 vs Bonsai | Workspace369",
  "/compare/zoho-projects": "Workspace369 vs Zoho Projects | Workspace369",
  "/compare/bloom": "Workspace369 vs Bloom | Workspace369",
  "/compare/smartsheet": "Workspace369 vs Smartsheet | Workspace369",
  "/compare/less-annoying-crm": "Workspace369 vs Less Annoying CRM | Workspace369",
  "/compare/wrike": "Workspace369 vs Wrike | Workspace369",
  "/compare/wave": "Workspace369 vs Wave | Workspace369",
  "/compare/insightly": "Workspace369 vs Insightly | Workspace369",
  "/compare/notion": "Workspace369 vs Notion | Workspace369",
  "/compare/motion": "Workspace369 vs Motion | Workspace369",
  "/compare/google-voice": "Workspace369 vs Google Voice | Workspace369",
  "/compare/quo": "Workspace369 vs Quo | Workspace369",
  "/compare/zilo": "Workspace369 vs Zilo | Workspace369",
  "/compare/monday-com": "Workspace369 vs monday.com | Workspace369",
  "/compare/capsule-crm": "Workspace369 vs Capsule CRM | Workspace369",
  "/compare/quickbooks": "Workspace369 vs QuickBooks | Workspace369",
  "/pricing": "Pricing | Workspace369",
  "/why-workspace369": "Why Choose Workspace369? | Workspace369",
  "/what-is-workspace369": "What Is Workspace369? | Workspace369",
  "/build-a-crm-with-ai": "Build a CRM with AI | Workspace369",
  "/workspace369-features": "Workspace369 Features | Workspace369",
  "/workspace369-pricing": "Workspace369 Pricing | Workspace369",
  "/integrations": "Integrations | Workspace369",
  "/customers": "Customer Stories | Workspace369",
  "/community": "Learn and follow Workspace369 | Community",
  "/roadmap": "Product Roadmap | Workspace369",
  "/support": "Get Support | Workspace369",
  "/about": "About | Workspace369",
  "/trust": "Trust | Workspace369",
  "/changelog": "Release Notes | Workspace369",
  ...releaseNotePageNames,
  "/blog": "Resources | Workspace369",
  "/tutorials": "Video Tutorials | Workspace369",
  "/templates": "Free Templates | Workspace369",
  "/templates/web-design-proposal": "Web Design Proposal Template: Free PDF & Word | Workspace369",
  "/templates/website-maintenance-agreement": "Website Maintenance Agreement Template: Free PDF & Word | Workspace369",
  "/templates/web-design-client-questionnaire": "Web Design Client Questionnaire Template: Free PDF & Word | Workspace369",
  "/templates/website-scope-of-work": "Website Scope of Work Template: Free PDF & Word | Workspace369",
  "/templates/website-change-request-form": "Website Change Request Form Template: Free PDF & Word | Workspace369",
  "/templates/web-design-deposit-request": "Web Design Deposit Request Template: Free PDF & Word | Workspace369",
  "/templates/web-design-revision-policy": "Web Design Revision Policy Template: Free PDF & Word | Workspace369",
  "/templates/web-design-proposal-follow-up-emails": "Web Design Proposal Follow-Up Emails: Free PDF & Word | Workspace369",
  "/templates/web-design-payment-reminder-emails": "Web Design Payment Reminder Emails: Free PDF & Word | Workspace369",
  "/templates/web-design-price-sheet": "Web Design Price Sheet Template: Free PDF & Word | Workspace369",
  "/templates/web-design-client-onboarding-checklist": "Web Design Client Onboarding Checklist: Free PDF & Word | Workspace369",
  "/templates/web-design-discovery-call-questions": "Web Design Discovery Call Questions: Free PDF & Word | Workspace369",
  "/templates/web-design-contract": "Web Design Contract Template: Free PDF & Word | Workspace369",
  "/templates/website-handover-checklist": "Website Handover Checklist Template: Free PDF & Word | Workspace369",
  "/templates/web-design-invoice": "Web Design Invoice Template: Free PDF & Word | Workspace369",
  "/templates/testimonial-request-email": "Testimonial Request Email Template: Free PDF & Word | Workspace369",
  "/templates/web-design-quote": "Web Design Quote Template: Free PDF & Word | Workspace369",
  "/templates/difficult-client-reply-scripts": "Difficult Client Reply Scripts: Free PDF & Word | Workspace369",
  "/templates/web-design-client-offboarding-email": "Client Offboarding Email Template: Free PDF & Word | Workspace369",
  "/templates/web-design-rate-increase-email": "Rate Increase Email Template: Free PDF & Word | Workspace369",
  "/templates/say-no-to-a-client-replies": "Say No to a Client Reply Templates: Free PDF & Word | Workspace369",
  "/templates/client-ghosting-email-sequence": "Client Ghosting Email Sequence: Free PDF & Word | Workspace369",
  "/templates/referral-request-email": "Referral Request Email Template: Free PDF & Word | Workspace369",
  "/templates/website-project-timeline": "Website Project Timeline Template: Free PDF & Word | Workspace369",
  "/templates/small-business-plan": "Small Business Plan Template: Free PDF & Word | Workspace369",
  "/templates/pitch-deck": "Pitch Deck Template: Free PDF & Word Outline | Workspace369",
  "/templates/hourly-vs-fixed-price-calculator": "Hourly vs Fixed Price Calculator: Free PDF & Word | Workspace369",
  "/templates/website-redesign-proposal": "Website Redesign Proposal Template: Free PDF & Word | Workspace369",
  "/templates/web-design-retainer-agreement": "Web Design Retainer Agreement Template: Free PDF & Word | Workspace369",
  "/templates/freelance-contract": "Freelance Contract Template: Free PDF & Word | Workspace369",
  "/tutorials/getting-started-tour": "Workspace369: the full tour | Workspace369",
  "/tutorials/set-up-your-business": "Set up your business in Workspace369 | Workspace369",
  "/tutorials/add-clients": "Add your clients to Workspace369 | Workspace369",
  "/tutorials/proposal-to-paid": "From proposal to paid in Workspace369 | Workspace369",
  "/tutorials/proposals-step-by-step": "Workspace369 proposals, step by step | Workspace369",
  "/tutorials/invoices-step-by-step": "Workspace369 invoices, step by step | Workspace369",
  "/tutorials/payment-reminders": "Automatic payment reminders in Workspace369 | Workspace369",
  "/tutorials/product-catalog": "Set up your product catalog in Workspace369 | Workspace369",
  "/tutorials/expenses": "Track your expenses in Workspace369 | Workspace369",
  "/tutorials/calendar": "Plan your schedule in the Workspace369 calendar | Workspace369",
  "/tutorials/online-booking-page": "Set up your online booking page in Workspace369 | Workspace369",
  "/tutorials/projects": "Run your projects in Workspace369 | Workspace369",
  "/tutorials/tasks": "Organize your tasks in Workspace369 | Workspace369",
  "/tutorials/time-tracking": "Track your time and bill it in Workspace369 | Workspace369",
  "/tutorials/reminders": "Set up reminders in Workspace369 | Workspace369",
  "/tutorials/inbox": "Reply to clients from one inbox in Workspace369 | Workspace369",
  "/tutorials/connect-your-email": "Connect your email address to Workspace369 | Workspace369",
  "/tutorials/calls-and-voicemail": "Calls and voicemail in Workspace369 | Workspace369",
  "/tutorials/leads-from-your-website": "Get leads from your website with Workspace369 | Workspace369",
  "/tutorials/funnels": "Build a lead funnel in Workspace369 | Workspace369",
  "/tutorials/workflows": "Automate your follow-ups in Workspace369 | Workspace369",
  "/tutorials/workspace-ai": "Ask Workspace AI about your business in Workspace369 | Workspace369",
  "/tutorials/invite-your-team": "Invite your team to Workspace369 | Workspace369",
  "/tutorials/client-portal": "Share a client portal in Workspace369 | Workspace369",
  "/tutorials/retainers-and-recurring-invoices": "Set up retainers and recurring invoices in Workspace369 | Workspace369",
  "/tutorials/contracts-and-e-signature": "Send a contract for e-signature in Workspace369 | Workspace369",
  "/tutorials/client-follow-ups": "Catch up on your client follow-ups in Workspace369 | Workspace369",
  "/blog/internal-client-scoped-access":
    "Client-Scoped Access for Contractors | Workspace369",
  "/contact-sales": "Contact Sales | Workspace369",
  "/affiliate": "Affiliate Program | Workspace369",
  "/privacy-policy": "Privacy Policy | Workspace369",
  "/terms-of-service": "Terms of Service | Workspace369",
};

const readableSegment = (segment) =>
  segment
    .split("-")
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(" ");

const pageName = computed(() => {
  const path = currentPath.value;
  if (path === "/pricing") return `${pricingCopy.value.title} | Workspace369`;
  if (pageNames[path]) return pageNames[path];

  const lastSegment = path.split("/").filter(Boolean).at(-1);
  return lastSegment ? `${readableSegment(lastSegment)} | Workspace369` : "Workspace369";
});
const pageDescription = computed(() =>
  currentPath.value === "/pricing"
    ? getPricingMetaDescription(pricingCopy.value)
    : seoCopy.value.description,
);
const socialImageUrl = computed(() => `${siteUrl}${ogImagePathForRoute(currentPath.value)}`);
const socialImageAlt = computed(
  () => `${pageName.value.replace(/\s*\|\s*Workspace369\s*$/i, "")} - Workspace369 page preview`,
);

const breadcrumbItems = computed(() => {
  const path = currentPath.value;
  const segments = path.split("/").filter(Boolean);
  const items = [
    {
      "@type": "ListItem",
      position: 1,
      name: "Home",
      item: localizedUrl(siteUrl, "/", canonicalLocaleCode.value),
    },
  ];

  segments.forEach((segment, index) => {
    const itemPath = `/${segments.slice(0, index + 1).join("/")}`;
    items.push({
      "@type": "ListItem",
      position: index + 2,
      name: pageNames[itemPath]?.replace(" | Workspace369", "") || readableSegment(segment),
      item: localizedUrl(siteUrl, itemPath, canonicalLocaleCode.value),
    });
  });

  return items;
});

const buildStructuredData = () => {
  const currentUrl = canonicalUrl.value;
  const language = currentLocale.value.hreflang;
  const graph = [
    {
      "@type": "Organization",
      "@id": `${siteUrl}/#organization`,
      name: "Workspace369",
      legalName: "Workspace369 LLC",
      alternateName: [
        "Workspace369 app",
        "W369",
        "Workspace 369",
        "Workspace369 client work operating system",
        "Workspace369 service business CRM",
        "Workspace369 branded proposals and client portal",
      ],
      disambiguatingDescription:
        "Workspace369 (one word, 369) is a client-work platform with a public MCP server and HTTP API; it is not Workspace 365. Workspace369 is separate from similarly named Workspace 365, Workspace360, Work360, and Microsoft 365 products. Workspace369 is client-work software for freelancers, consultants, studios, agencies, and service businesses: branded proposals and booking pages that clients accept, sign, and pay online, plus the CRM, projects, invoicing, and automations that run the work afterward.",
      url: `${siteUrl}/`,
      logo: {
        "@type": "ImageObject",
        "@id": `${siteUrl}/#logo`,
        url: `${siteUrl}/favicon/apple-touch-icon.png`,
        width: 180,
        height: 180,
      },
      contactPoint: [
        {
          "@type": "ContactPoint",
          contactType: "sales",
          email: "sales@workspace369.com",
        },
        {
          "@type": "ContactPoint",
          contactType: "customer support",
          email: "support@workspace369.com",
        },
      ],
      foundingDate: "2023-11-06",
      founder: { "@type": "Person", name: "Szymon Dziak", alternateName: "Simon Dziak", url: "https://simondziak.com/", sameAs: ["https://www.linkedin.com/in/szymondziak/"] },
      location: {
        "@type": "Place",
        address: { "@type": "PostalAddress", addressLocality: "Boynton Beach", addressRegion: "FL", addressCountry: "US" },
      },
      sameAs: [
        "https://apps.apple.com/us/app/workspace369/id6475013419",
        "https://www.youtube.com/@Workspace369",
        "https://www.facebook.com/workspace369/",
        "https://www.instagram.com/workspace369/",
        "https://www.g2.com/products/workspace369/reviews",
        "https://www.trustpilot.com/review/workspace369.com",
        "https://www.saashub.com/workspace369",
        "https://x.com/workspace369",
        "https://github.com/Workspace369-LLC",
        "https://devhunt.org/tool/workspace369",
        "https://glama.ai/mcp/connectors/com.workspace369/mcp",
        "https://themcpindex.com/servers/com-workspace369-mcp",
      ],
      subjectOf: [
        {
          "@type": "WebPage",
          name: "Workspace369 Trust",
          url: localizedUrl(siteUrl, "/trust", canonicalLocaleCode.value),
        },
        {
          "@type": "WebPage",
          name: "Workspace369 Privacy Policy",
          url: localizedUrl(siteUrl, "/privacy-policy", canonicalLocaleCode.value),
        },
        {
          "@type": "WebPage",
          name: "Workspace369 Terms of Service",
          url: localizedUrl(siteUrl, "/terms-of-service", canonicalLocaleCode.value),
        },
      ],
      knowsAbout: [
        "client work management",
        "branded client proposals",
        "online proposal acceptance and e-signature",
        "deposit collection on proposal acceptance",
        "client-facing branding",
        "online booking pages",
        "client onboarding automation",
        "client experience",
        "service business management software",
        "client work operating system",
        "service-business CRM",
        "client-work CRM",
        "field service CRM",
        "CRM for solo operators",
        "CRM for consultants",
        "CRM for agencies",
        "CRM for small service teams",
        "CRM",
        "project management",
        "scheduling",
        "shared inbox",
        "invoicing",
        "payments",
        "accounting",
        "automation",
        "AI for service businesses",
        "inventory management",
        "business phone system with CRM",
        "AI call summary software",
        "SMS inbox for business",
        "client-scoped access",
        "contractor permissions",
        "record-level client permissions",
        "agency contractor access control",
        "Google Voice alternative",
        "software stack consolidation",
        "HubSpot alternative",
        "Salesforce alternative",
        "HoneyBook alternative",
        "Dubsado alternative",
        "Bonsai alternative",
        "Zoho Projects alternative",
        "Bloom alternative",
        "Smartsheet alternative",
        "Less Annoying CRM alternative",
        "Wrike alternative",
        "Wave alternative",
        "Insightly alternative",
        "Notion alternative",
        "Paymo alternative",
        "Motion alternative",
        "Google Workspace spreadsheets alternative",
        "Quo alternative",
        "Zilo alternative",
        "Invoice Ninja alternative",
        "monday.com alternative",
        "QuickBooks alternative",
        "Bigin alternative",
        "Capsule CRM alternative",
        "CRM for solo entrepreneurs",
      ],
    },
    {
      "@type": "WebSite",
      "@id": `${siteUrl}/#website`,
      url: `${siteUrl}/`,
      name: "Workspace369",
      publisher: { "@id": `${siteUrl}/#organization` },
      inLanguage: language,
    },
    {
      "@type": "SoftwareApplication",
      "@id": `${siteUrl}/#software`,
      name: "Workspace369",
      alternateName: [
        "Workspace369 app",
        "W369",
        "Workspace 369",
        "Workspace369 service-business CRM",
        "Workspace369 client-work CRM",
        "Workspace369 operating workspace",
      ],
      url: `${siteUrl}/`,
      applicationCategory: "BusinessApplication",
      applicationSubCategory: "Client-work management: branded proposals, booking, invoicing, and CRM",
      operatingSystem: "Web, iOS",
      installUrl: "https://apps.apple.com/us/app/workspace369/id6475013419",
      releaseNotes: `${siteUrl}/changelog/`,
      disambiguatingDescription:
        "Workspace369 (one word, 369) is a client-work platform with a public MCP server and HTTP API; it is not Workspace 365. Workspace369 is a Workspace369 LLC software product for branded proposals, booking pages, client portals, billing, CRM, client-work operations, inbox, AI, automation, and reporting. It is not Workspace 365, Workspace360, Work360, Microsoft 365, or Google Workspace.",
      description:
        "Workspace369 is client-work software for freelancers, consultants, studios, agencies, and service teams of any size. Clients open one branded proposal link, choose optional add-ons, accept with a signature, pay a deposit by card, and receive the attached contract to sign; booking pages let them pick a service and pay online. The same workspace then runs CRM, projects, invoicing, payments, inbox, AI, automations, accounting, and inventory.",
      inLanguage: language,
      audience: {
        "@type": "BusinessAudience",
        audienceType:
          "freelancers, consultants, creative studios, agencies, and service businesses from solo operators to multi-team companies",
      },
      featureList: [
        "Branded proposals with optional add-ons, online acceptance, and signature",
        "Deposits on proposal acceptance by card through Stripe (every plan from Cadet)",
        "Contracts with e-signature, sent automatically when a proposal is accepted",
        "Brand themes, accent colors, logo, and cover images on client-facing pages",
        "Online booking pages with deposits or full payment (Specialist plan and up)",
        "Client portal for projects, invoices, contracts, and files",
        "Workflows triggered by proposal accepted, contract signed, and payment received",
        "Proposals, invoices, and payment links",
        "CRM and client records",
        "Requests and forms",
        "Projects, tasks, notes, and calendar",
        "Inbox optional for proposals and booking; shared inbox, SMS, voice, and voicemail (Voyager plan and up) as teams grow",
        "Client-scoped access for contractors and account managers",
        "AI chat, compose, rewrite, translate, and knowledge search",
        "Workflow automations: email and SMS steps (SMS on the Voyager plan and up), task creation, reminders, and internal notifications",
        "Accounting, expenses, products, services, and inventory",
        "Dashboards, reports, and analytics",
      ],
      offers: buildPrimarySoftwareOffer(
        pricingPlans,
        localizedUrl(siteUrl, "/pricing", canonicalLocaleCode.value),
      ),
      publisher: { "@id": `${siteUrl}/#organization` },
    },
    {
      "@type": "WebPage",
      "@id": `${currentUrl}#webpage`,
      url: currentUrl,
      name: pageName.value,
      isPartOf: { "@id": `${siteUrl}/#website` },
      about: { "@id": `${siteUrl}/#software` },
      inLanguage: language,
      ...(routeLastmod.value ? { dateModified: routeLastmod.value } : {}),
    },
    ...(articleNode.value ? [articleNode.value] : []),
  ];

  if (breadcrumbItems.value.length > 1) {
    graph.push({
      "@type": "BreadcrumbList",
      "@id": `${currentUrl}#breadcrumb`,
      itemListElement: breadcrumbItems.value,
    });
  }

  if (currentPath.value === "/pricing") {
    graph.push({
      "@type": "FAQPage",
      "@id": `${currentUrl}#faq`,
      // Founding 100 answers stay out of the schema: the edge function can hide them on the page at
      // the deadline, but not inside JSON-LD.
      mainEntity: pricingCopy.value.faq.items.filter((item) => !item.founding).map((item) => ({
        "@type": "Question",
        name: item.q,
        acceptedAnswer: {
          "@type": "Answer",
          text: item.a,
        },
      })),
    });
  }

  return {
    "@context": "https://schema.org",
    "@graph": graph,
  };
};

useHead(() => ({
  htmlAttrs: {
    lang: currentLocale.value.hreflang,
    dir: currentLocale.value.dir,
  },
  title: currentPath.value === "/" ? seoCopy.value.title : pageName.value,
  link: [
    { key: "canonical", rel: "canonical", href: canonicalUrl.value },
    ...alternateLinks.value,
  ],
  meta: [
    {
      key: "robots",
      name: "robots",
      content: isCurrentPageIndexable.value && !noindexRoutePaths.includes(currentPath.value) ? "index, follow" : "noindex, follow",
    },
    {
      key: "description",
      name: "description",
      content: pageDescription.value,
    },
    { key: "og-site-name", property: "og:site_name", content: "Workspace369" },
    { key: "og-image", property: "og:image", content: socialImageUrl.value },
    { key: "og-image-secure-url", property: "og:image:secure_url", content: socialImageUrl.value },
    { key: "og-image-width", property: "og:image:width", content: "1200" },
    { key: "og-image-height", property: "og:image:height", content: "630" },
    { key: "og-image-type", property: "og:image:type", content: "image/jpeg" },
    { key: "og-image-alt", property: "og:image:alt", content: socialImageAlt.value },
    {
      key: "og-type",
      property: "og:type",
      content: currentPath.value.startsWith("/blog/") ? "article" : "website",
    },
    { key: "og-url", property: "og:url", content: canonicalUrl.value },
    {
      key: "og-locale",
      property: "og:locale",
      content: ogLocales[currentLocale.value.code] || currentLocale.value.hreflang,
    },
    { key: "twitter-card", name: "twitter:card", content: "summary_large_image" },
    { key: "twitter-image", name: "twitter:image", content: socialImageUrl.value },
    { key: "twitter-image-alt", name: "twitter:image:alt", content: socialImageAlt.value },
  ],
  // Embedded calculators carry no sitewide structured data (their canonical is the tool page).
  script: isEmbedPath(route.path) ? [] : [
    {
      key: "workspace369-structured-data",
      type: "application/ld+json",
      children: JSON.stringify(buildStructuredData()),
    },
  ],
}));

// onMounted(() => {
//   window.rewardful("ready", () => {
//     // Store referral ID in localStorage when available
//     if (window.Rewardful?.referral) {
//       console.log("Referral ID:", window.Rewardful.referral);
//       console.log("Referral Affiliate:", window.Rewardful.affiliate);
//       console.log("Referral Campaign:", window.Rewardful.campaign);

//       // localStorage.setItem('rewardfulReferral', window.Rewardful.referral);
//     }
//   });
// });
</script>

<style>
:root {
  --active-link: #000;
}
html.dark {
  --active-link: #fff;
  @apply bg-slate-950;
}
html,
body,
#__nuxt {
  height: 100vh;
  margin: 0;
  padding: 0;
}
/* A word wider than its line (Workspace369:stä, Automatisierungsfragen at 125% phone text) wraps instead of
   running under the page's overflow clip. It only breaks a word that can't fit, so normal text is unchanged. */
h1, h2, h3, h4, summary {
  overflow-wrap: break-word;
}
</style>
